/**
 * TourProvider - React Joyride Integration
 * Wraps the app with Joyride functionality and manages tour execution
 * Handles tour completion callbacks and user metadata updates
 */
import React, { useCallback, useEffect, useState } from "react";
import Joyride, { CallBackProps, STATUS, EVENTS, Step } from "react-joyride";
import { useTour } from "@/global/contexts/TourContext";
import { tourStyles } from "./tourStyles";
import { getTourSteps } from "./steps";
import { useAuth } from "@/global/auth/AuthContext";
import { supabase } from "@/lib/supabase";
import { isAgentOrganization } from "@/global/lib/agentUtils";

interface JoyrideProviderProps {
  children: React.ReactNode;
}

export function JoyrideProvider({ children }: JoyrideProviderProps) {
  const { currentTour, isRunning, stepIndex, endTour, setStepIndex, skipTour } =
    useTour();
  const { user, activeTenantId } = useAuth();
  const [isAgent, setIsAgent] = useState<boolean>(true); // Default to agent

  // Detect if current organization is an agent
  useEffect(() => {
    if (!activeTenantId) return;

    const checkAgentStatus = async () => {
      try {
        const agentStatus = await isAgentOrganization(activeTenantId);
        setIsAgent(agentStatus);
      } catch (error) {
        console.error('[TourProvider] Error checking agent status:', error);
        setIsAgent(true); // Default to agent on error
      }
    };

    checkAgentStatus();
  }, [activeTenantId]);

  // Get current tour steps with role-based content
  const steps: Step[] = currentTour ? getTourSteps(currentTour, isAgent) : [];

  // Handle tour completion and update user metadata
  const handleTourComplete = useCallback(
    async (tourId: string) => {
      try {
        // CRITICAL: Check if we have a valid session before updating
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || !user) {
          console.warn('[TourProvider] No valid session, skipping tour completion update');
          return;
        }

        console.log('[TourProvider] Completing tour:', tourId);
        
        // Update user metadata to mark tour as completed
        if (tourId === "ams-main" && user) {
          await supabase.auth.updateUser({
            data: {
              has_completed_ams_tour: true,
              ams_tour_version: "1.0",
              tour_completed_at: new Date().toISOString(),
            },
          });
          console.log('[TourProvider] AMS tour completion saved');
        } else if (tourId === "fma-home" && user) {
          await supabase.auth.updateUser({
            data: {
              has_completed_fma_tour: true,
              fma_tour_version: "1.0",
              tour_completed_at: new Date().toISOString(),
            },
          });
          console.log('[TourProvider] FMA tour completion saved');
        }

        // Track completion event (optional PostHog integration)
        if (typeof window !== "undefined" && (window as any).posthog) {
          (window as any).posthog.capture("tour_completed", {
            tour_id: tourId,
            user_id: user?.id,
          });
        }
      } catch (error) {
        console.error('[TourProvider] Error updating tour completion:', error);
      }
    },
    [user]
  );

  // Add click listeners for tab steps to auto-advance tour
  useEffect(() => {
    if (!isRunning || !steps[stepIndex]) return;

    const currentStep = steps[stepIndex];
    const target = currentStep.target as string;

    // Only add listeners for tab steps that need dual functionality
    if (
      target === '[data-tour="profile-tab"]' ||
      target === '[data-tour="points-tab"]'
    ) {
      const element = document.querySelector(target);
      if (element) {
        const handleTabClick = () => {
          // Wait for tab change to complete, then advance tour
          setTimeout(() => {
            setStepIndex(stepIndex + 1);
          }, 100);
        };

        element.addEventListener('click', handleTabClick);
        return () => {
          element.removeEventListener('click', handleTabClick);
        };
      }
    }
  }, [isRunning, stepIndex, steps, setStepIndex]);

  // Handle tour callbacks from Joyride
  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status, type, index, action } = data;

      if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type as any)) {
        // Update step index as user progresses
        setStepIndex(index + (action === "prev" ? -1 : 1));
      } else if (
        [STATUS.FINISHED, STATUS.SKIPPED].includes(status as any)
      ) {
        // Tour finished or skipped
        if (status === STATUS.FINISHED && currentTour) {
          handleTourComplete(currentTour);
        }

        // Track skipped tours
        if (
          status === STATUS.SKIPPED &&
          typeof window !== "undefined" &&
          (window as any).posthog
        ) {
          (window as any).posthog.capture("tour_skipped", {
            tour_id: currentTour,
            step_index: index,
            user_id: user?.id,
          });
        }

        // End the tour
        if (status === STATUS.SKIPPED) {
          skipTour();
        } else {
          endTour();
        }
      }
    },
    [currentTour, endTour, skipTour, setStepIndex, handleTourComplete, user]
  );

  return (
    <>
      {children}
      {isRunning && steps.length > 0 && (
        <Joyride
          steps={steps}
          run={isRunning}
          stepIndex={stepIndex}
          continuous
          showProgress
          showSkipButton
          disableOverlayClose
          disableCloseOnEsc={false}
          spotlightClicks={false}
          spotlightPadding={8}
          callback={handleJoyrideCallback}
          styles={tourStyles}
          locale={{
            back: "Back",
            close: "Close",
            last: "Finish",
            next: "Next",
            skip: "Skip tour",
          }}
        />
      )}
    </>
  );
}
